import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Engineering,
  CheckCircle,
  CalendarToday
} from '@mui/icons-material';
import api from '../services/api';
import LocationInput from '../components/common/LocationInput';
import AIDescriptionField from '../components/common/AIDescriptionField';

const CreateVerificationRequest = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedVerifierId = searchParams.get('verifierId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedVerifier, setSelectedVerifier] = useState(null);

  const [formData, setFormData] = useState({
    projectId: '',
    title: '',
    description: '',
    priority: 'normal',
    requestedDate: '',
    preferredStartTime: '',
    preferredEndTime: '',
    location: null,
    estimatedFee: '',
    // Marketplace fields
    selectionMode: preselectedVerifierId ? 'direct' : 'marketplace',
    proposalDeadline: '',
    minVerifierRating: '',
    maxProposals: ''
  });

  useEffect(() => {
    fetchProjects();
    if (preselectedVerifierId) {
      fetchVerifierDetails(preselectedVerifierId);
    }
  }, [preselectedVerifierId]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      // API returns { success: true, data: { projects: [...], count: N } }
      const projectsData = response.data?.data?.projects || [];
      // Ensure it's an array
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]); // Ensure projects is always an array
    }
  };

  const fetchVerifierDetails = async (verifierId) => {
    try {
      const response = await api.get(`/verifiers/${verifierId}`);
      setSelectedVerifier(response.data.data);
    } catch (err) {
      console.error('Error fetching verifier:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getLocationAddress = (location) => {
    if (typeof location === 'string') return location.trim();
    return location?.address?.trim() || location?.name?.trim() || '';
  };

  const getLocationCity = (location) => (
    location?.city
    || location?.addressComponents?.locality
    || location?.addressComponents?.administrativeAreaLevel2
    || getLocationAddress(location)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.projectId) {
      setError('Project verification is for active Eqaya projects. Choose a project first, or book a Property Check for an existing home, rental, or Airbnb.');
      setLoading(false);
      return;
    }

    // Validate time range
    if (
      formData.preferredStartTime &&
      formData.preferredEndTime &&
      formData.preferredStartTime >= formData.preferredEndTime
    ) {
      setError('End time must be after start time');
      setLoading(false);
      return;
    }

    try {
      const locationAddress = getLocationAddress(formData.location);
      const locationData = typeof formData.location === 'object' && formData.location
        ? formData.location
        : { address: locationAddress };
      const hasPreferredTimeSlot = formData.preferredStartTime && formData.preferredEndTime;
      const hasEstimatedFee = formData.estimatedFee !== ''
        && formData.estimatedFee !== null
        && Number.isFinite(Number(formData.estimatedFee));

      const requestData = {
        requestDetails: {
          title: formData.title,
          description: formData.description,
          verificationScope: [formData.description],
          urgency: formData.priority
        },
        location: {
          address: locationAddress,
          city: getLocationCity(locationData),
          coordinates: locationData.coordinates || null,
          addressComponents: locationData.addressComponents || null,
          placeId: locationData.placeId || null,
          source: locationData.source || 'manual'
        },
        scheduling: {
          requestedDate: formData.requestedDate || null,
          preferredTimeSlots: hasPreferredTimeSlot ? [{
            start: formData.preferredStartTime,
            end: formData.preferredEndTime
          }] : [],
          flexibility: formData.requestedDate ? 'preferred_date' : 'verifier_proposes'
        },
        payment: {
          verificationFee: hasEstimatedFee ? Number(formData.estimatedFee) : null,
          currency: 'USD',
          paymentStatus: 'pending'
        },
        priority: formData.priority,
        tags: ['project_verification'],
        clientSelectionMode: preselectedVerifierId ? 'direct' : formData.selectionMode,
        isOpenToBids: !preselectedVerifierId && formData.selectionMode === 'marketplace'
      };

      if (formData.projectId) {
        requestData.projectId = formData.projectId;
      }

      if (hasEstimatedFee) {
        requestData.estimatedFee = Number(formData.estimatedFee);
        requestData.payment = {
          verificationFee: Number(formData.estimatedFee),
          currency: 'USD',
          paymentStatus: 'pending'
        };
      }

      // Add marketplace-specific fields if marketplace mode is selected
      if (formData.selectionMode === 'marketplace') {
        requestData.isOpenToBids = true;
        if (formData.proposalDeadline) {
          requestData.proposalDeadline = formData.proposalDeadline;
        }
        if (formData.minVerifierRating) {
          requestData.minVerifierRating = parseFloat(formData.minVerifierRating);
        }
        if (formData.maxProposals) {
          requestData.maxProposals = parseInt(formData.maxProposals, 10);
        }
      }

      const response = await api.post('/verification-requests', requestData);

      // If a specific verifier was pre-selected, assign them
      if (preselectedVerifierId && response.data.data?.id) {
        try {
          await api.post(
            `/verification-requests/${response.data.data.id}/assign`,
            {
              verifierId: preselectedVerifierId,
              method: 'manual',
              criteria: 'client_selected'
            }
          );
        } catch (assignErr) {
          console.error('Error assigning verifier:', assignErr);
          // Continue anyway, request was created
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/verification-requests');
      }, 2000);
    } catch (err) {
      console.error('Error creating verification request:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create verification request';
      const errorDetails = err.response?.data?.errors ? 
        err.response.data.errors.map(e => e.msg).join(', ') : '';
      setError(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Please login to create a verification request.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Engineering sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1">
            Request Project Verification
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Use this when you need a verifier to check work tied to one of your Eqaya construction projects.
          For a house, rental, or Airbnb that is not part of a project, book a Property Check instead.
        </Alert>

        {selectedVerifier && (
          <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Selected Verifier
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h6">
                    {selectedVerifier.business_name || `${selectedVerifier.first_name} ${selectedVerifier.last_name}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedVerifier.specializations?.join(', ')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Project verification request created successfully! Redirecting to Verifications...
          </Alert>
        )}

        {projects.length === 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button color="inherit" size="small" onClick={() => navigate('/property-check/book')}>
                  Book a Property Check
                </Button>
                <Button color="inherit" size="small" onClick={() => navigate('/dashboard')}>
                  New Project
                </Button>
              </Box>
            }
          >
            You do not have an Eqaya project yet. Start a project for construction work, or book a Property Check for an existing property.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  label="Project"
                  required
                >
                  <MenuItem value="" disabled>
                    Choose an Eqaya project
                  </MenuItem>
                  {projects.length === 0 ? (
                    <MenuItem disabled>
                      No projects available
                    </MenuItem>
                  ) : (
                    projects.map(project => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.title || project.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Check roofing progress"
              />
            </Grid>

            <Grid item xs={12}>
              <AIDescriptionField
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                label="What do you need verified?"
                required
                placeholder="Describe the work, concern, or site condition you want the verifier to inspect."
                projectType="project_verification"
                taskType={formData.title || 'construction verification'}
                location={
                  getLocationAddress(formData.location)
                  || projects.find(p => p.id === formData.projectId)?.location
                }
                onError={setError}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <LocationInput
                value={formData.location}
                onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                label="Location"
                placeholder="Enter site address"
                required
                helperText="Search for the site address or enter coordinates exactly as you can in New Project."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Requested Date (Optional)"
                name="requestedDate"
                value={formData.requestedDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <CalendarToday sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                helperText="Leave blank if you want verifiers to propose available dates."
                inputProps={{
                  min: new Date().toISOString().split('T')[0] // Prevent past dates
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1, mb: 1 }}>
                Preferred Time Slots (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you do not know the best time yet, leave these blank and let verifiers suggest availability.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Preferred Start Time (Optional)"
                name="preferredStartTime"
                value={formData.preferredStartTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Earliest time for verification"
                inputProps={{
                  step: 300 // 5 minute intervals
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Preferred End Time (Optional)"
                name="preferredEndTime"
                value={formData.preferredEndTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Latest time for verification"
                inputProps={{
                  step: 300 // 5 minute intervals
                }}
              />
            </Grid>

            {formData.selectionMode === 'marketplace' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Verification jobs are a fixed <strong>$75 fee</strong> — the job is posted to nearby verifiers
                    and the first qualified verifier to accept is assigned. No quotes to compare, no waiting.
                  </Alert>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/verifiers')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    loading || 
                    !formData.projectId ||
                    !formData.title || 
                    !formData.description ||
                    !getLocationAddress(formData.location)
                  }
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {loading ? 'Creating...' : formData.selectionMode === 'marketplace' ? 'Post Verification Request' : 'Send Verification Request'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateVerificationRequest;
